import React from 'react';
import { Customer } from '../../types';

interface SignupInfoProps {
  customer: Customer;
  progress: number;
}

const SignupInfo: React.FC<SignupInfoProps> = ({ customer, progress }) => {
  const formatDate = (dateString: string): string => {
    if (!dateString || dateString.trim() === '') {
      return 'Not specified';
    }
    
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date format';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date format';
    }
  };

  const calculateDaysSince = (dateString: string): number => {
    if (!dateString || dateString.trim() === '') {
      return 0;
    }
    
    try {
      const signupDate = new Date(dateString);
      // Check if the date is valid
      if (isNaN(signupDate.getTime())) {
        return 0;
      }
      const now = new Date();
      const diffTime = now.getTime() - signupDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch {
      return 0;
    }
  };

  const getJourneyStatus = (days: number, progress: number): { emoji: string; text: string; class: string } => {
    if (progress === 100) {
      return { emoji: '🎉', text: 'Completed!', class: 'completed' };
    }
    if (days <= 7) {
      return { emoji: '🚀', text: 'Just Started', class: 'new' };
    }
    if (days <= 14 && progress >= 50) {
      return { emoji: '⚡', text: 'Fast Progress', class: 'fast' };
    }
    if (days <= 30) {
      return { emoji: '📈', text: 'On Track', class: 'on-track' };
    }
    if (progress >= 75) {
      return { emoji: '🎯', text: 'Nearly There', class: 'nearly' };
    }
    return { emoji: '⏳', text: 'Taking Time', class: 'slow' };
  };

  const daysSince = calculateDaysSince(customer.signupDate);
  const journeyStatus = getJourneyStatus(daysSince, progress);

  return (
    <div className="journey-info-card">
      <h3 className="journey-title">Your Journey</h3>
      
      <div className="journey-timeline">
        <div className="timeline-item start">
          <div className="timeline-icon">📅</div>
          <div className="timeline-content">
            <span className="timeline-label">Started</span>
            <span className="timeline-value">{formatDate(customer.signupDate)}</span>
          </div>
        </div>

        <div className="timeline-connector">
          <div className="timeline-line">
            <div 
              className="timeline-progress" 
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <div className="timeline-days">
            <span className="days-count">{daysSince}</span>
            <span className="days-label">days</span>
          </div>
        </div>

        <div className={`timeline-item status ${journeyStatus.class}`}>
          <div className="timeline-icon">{journeyStatus.emoji}</div>
          <div className="timeline-content">
            <span className="timeline-label">Status</span>
            <span className="timeline-value">{journeyStatus.text}</span>
          </div>
        </div>
      </div>

      {daysSince > 0 && progress < 100 && (
        <div className="journey-insights">
          <p className="insight-text">
            {progress === 0 && "Ready to start? Your first task awaits! 🎯"}
            {progress > 0 && progress < 25 && "Great start! Keep the momentum going! 💪"}
            {progress >= 25 && progress < 50 && "You're making good progress! 📈"}
            {progress >= 50 && progress < 75 && "Halfway there! You're doing amazing! 🌟"}
            {progress >= 75 && progress < 100 && "Almost finished! Just a few more steps! 🏁"}
          </p>
        </div>
      )}
    </div>
  );
};

export default SignupInfo;